// ============================================================================
// Stats modal — CPU, RAM, disk, processes widget
// ============================================================================
// Depends on: $ (dom.js), escapeHtml (markdown.js), haptic (shared.js global),
// statsContent/statsOverlay/statsClose/sbStatsBtn (dom.js),
// wireModal/openModal/isModalOpen (modal.js).

import { $ } from "./dom.js";
import { escapeHtml } from "./markdown.js";
import { wireModal, openModal, isModalOpen } from "./modal.js";

let statsTimer = null;
let lastStats = null;
let procSortKey = "mem"; // "cpu" or "mem"
let procSortDesc = true;
let statsStructureBuilt = false;

export function fmtBytes(bytes) {
  if (bytes < 1024) return bytes + "B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + "K";
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(0) + "M";
  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + "G";
}

function fmtUptime(seconds) {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function barClass(pct) {
  if (pct < 0.6) return "low";
  if (pct < 0.85) return "mid";
  return "high";
}

async function fetchStats() {
  try {
    const token = localStorage.getItem("devin-token");
    if (!token) return null;
    const res = await fetch("/api/stats", {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

function getSortedProcesses() {
  if (!lastStats || !lastStats.processes) return [];
  const procs = [...lastStats.processes];
  procs.sort((a, b) => {
    const diff = a[procSortKey] - b[procSortKey];
    return procSortDesc ? -diff : diff;
  });
  return procs;
}

// Pastel color by absolute percentage: green <20, yellow <50, orange <80, red >=80
function pastelColor(pct) {
  if (pct < 20)  return { bg: "rgba(99, 199, 132, 0.25)",  text: "#7ee2a0" };
  if (pct < 50)  return { bg: "rgba(224, 196, 100, 0.25)", text: "#e6d480" };
  if (pct < 80)  return { bg: "rgba(224, 154, 100, 0.30)", text: "#e6b080" };
  return            { bg: "rgba(224, 100, 100, 0.30)", text: "#e68080" };
}

function barCellHTML(val, max, pct, displayText) {
  const ratio = Math.min(1, val / max);
  const c = pastelColor(pct);
  return `<td class="proc-bar-cell" style="background: linear-gradient(90deg, ${c.bg} ${ratio * 100}%, transparent ${ratio * 100}%);"><span class="proc-bar-val" style="color:${c.text}">${displayText}</span></td>`;
}

function updateBarCell(td, val, max, pct, displayText) {
  const ratio = Math.min(1, val / max);
  const c = pastelColor(pct);
  td.style.background = `linear-gradient(90deg, ${c.bg} ${ratio * 100}%, transparent ${ratio * 100}%)`;
  const span = td.firstElementChild;
  span.style.color = c.text;
  span.textContent = displayText;
}

function ensureStatsStructure(statsContent) {
  if (statsStructureBuilt || !statsContent) return;
  statsStructureBuilt = true;
  statsContent.innerHTML = `
    <div class="stats-card">
      <div class="stats-card-title">CPU</div>
      <div><span class="stats-big-num" id="st-cpu-pct">0</span><span class="stats-big-unit">%</span></div>
      <div class="stats-bar-large"><div class="sb-stats-fill" id="st-cpu-bar"></div></div>
      <div class="stats-detail">
        Cores: <span id="st-cpu-cores">—</span><br>
        Load: <span id="st-cpu-load">—</span><br>
        Model: <span id="st-cpu-model">—</span>
      </div>
    </div>
    <div class="stats-card">
      <div class="stats-card-title">Memory</div>
      <div><span class="stats-big-num" id="st-mem-pct">0</span><span class="stats-big-unit">%</span></div>
      <div class="stats-bar-large"><div class="sb-stats-fill" id="st-mem-bar"></div></div>
      <div class="stats-detail">
        Used: <span id="st-mem-used">—</span> / <span id="st-mem-total">—</span><br>
        Free: <span id="st-mem-free">—</span>
      </div>
    </div>
    <div class="stats-card" id="st-disk-card" style="display:none">
      <div class="stats-card-title">Disk</div>
      <div><span class="stats-big-num" id="st-disk-pct">0</span><span class="stats-big-unit">%</span></div>
      <div class="stats-bar-large"><div class="sb-stats-fill" id="st-disk-bar"></div></div>
      <div class="stats-detail">
        Used: <span id="st-disk-used">—</span> / <span id="st-disk-total">—</span><br>
        Available: <span id="st-disk-avail">—</span>
      </div>
    </div>
    <div class="stats-card">
      <div class="stats-card-title">System</div>
      <div class="stats-detail" style="font-size:13px;line-height:2">
        Hostname: <span id="st-sys-host">—</span><br>
        Platform: <span id="st-sys-platform">—</span><br>
        Uptime: <span id="st-sys-uptime">—</span><br>
        Shells: <span id="st-sys-shells">—</span>
      </div>
    </div>
    <div class="stats-card stats-card-wide">
      <div class="stats-card-title">Top Processes <span style="font-weight:400;font-size:10px;opacity:0.6">(click CPU% or MEM% to sort)</span></div>
      <table class="stats-proc-table">
        <thead>
          <tr>
            <th>PID</th>
            <th>User</th>
            <th class="th-sortable" data-sort="cpu" id="st-th-cpu">CPU%</th>
            <th class="th-sortable" data-sort="mem" id="st-th-mem">MEM%</th>
            <th>RAM</th>
            <th>Command</th>
            <th></th>
          </tr>
        </thead>
        <tbody id="st-proc-tbody"></tbody>
      </table>
    </div>
  `;
  // Wire up sort headers once
  statsContent.querySelectorAll(".th-sortable").forEach((th) => {
    th.addEventListener("click", () => {
      haptic("tap");
      const key = th.dataset.sort;
      if (procSortKey === key) {
        procSortDesc = !procSortDesc;
      } else {
        procSortKey = key;
        procSortDesc = true;
      }
      updateSortArrows();
      updateProcTable();
    });
  });
  updateSortArrows();
}

function updateSortArrows() {
  const cpuTh = $("st-th-cpu");
  const memTh = $("st-th-mem");
  if (cpuTh) cpuTh.textContent = "CPU%" + (procSortKey === "cpu" ? (procSortDesc ? " ↓" : " ↑") : "");
  if (memTh) memTh.textContent = "MEM%" + (procSortKey === "mem" ? (procSortDesc ? " ↓" : " ↑") : "");
}

function wireKillButton(btn) {
  btn.addEventListener("click", async () => {
    const pid = btn.dataset.pid;
    const cmd = btn.dataset.cmd;
    if (!confirm(`Kill PID ${pid} (${cmd})?\nThis will send SIGTERM, then SIGKILL if needed.`)) return;
    haptic("press");
    btn.disabled = true;
    btn.textContent = "…";
    try {
      const token = localStorage.getItem("devin-token");
      const res = await fetch("/api/kill", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ pid: Number(pid) }),
      });
      const data = await res.json();
      if (data.ok) {
        btn.textContent = "✓";
        btn.classList.add("killed");
        haptic("success");
        setTimeout(() => fetchStats().then(renderStats), 500);
      } else {
        btn.textContent = "✗";
        haptic("error");
        alert(data.error || "Failed to kill process");
        setTimeout(() => { btn.textContent = "×"; btn.disabled = false; }, 1500);
      }
    } catch (err) {
      btn.textContent = "✗";
      setTimeout(() => { btn.textContent = "×"; btn.disabled = false; }, 1500);
    }
  });
}

function updateProcTable() {
  const tbody = $("st-proc-tbody");
  if (!tbody) return;
  const procs = getSortedProcesses();
  const maxCpu = Math.max(...procs.map(p => p.cpu), 1);
  const maxMem = Math.max(...procs.map(p => p.mem), 1);
  const maxRss = Math.max(...procs.map(p => p.rss), 1);

  const existing = new Map();
  tbody.querySelectorAll("tr").forEach((tr) => {
    const pid = tr.dataset.pid;
    if (pid) existing.set(pid, tr);
  });

  const seen = new Set();
  const frag = document.createDocumentFragment();

  for (const p of procs) {
    const pidStr = String(p.pid);
    seen.add(pidStr);
    const ramPct = (p.rss / maxRss) * 100;
    let tr = existing.get(pidStr);

    if (tr) {
      const cells = tr.children;
      cells[0].textContent = p.pid;
      cells[1].textContent = p.user;
      updateBarCell(cells[2], p.cpu, maxCpu, p.cpu, p.cpu.toFixed(1));
      updateBarCell(cells[3], p.mem, maxMem, p.mem, p.mem.toFixed(1));
      updateBarCell(cells[4], p.rss, maxRss, ramPct, fmtBytes(p.rss));
      const cmdCell = cells[5];
      cmdCell.textContent = p.command;
      cmdCell.title = p.command;
      const killBtn = cells[6].querySelector(".proc-kill-btn");
      if (killBtn) {
        killBtn.dataset.cmd = p.command;
        killBtn.title = `Kill PID ${p.pid}`;
      }
    } else {
      tr = document.createElement("tr");
      tr.dataset.pid = pidStr;
      tr.innerHTML = `
        <td>${p.pid}</td>
        <td>${escapeHtml(p.user)}</td>
        ${barCellHTML(p.cpu, maxCpu, p.cpu, p.cpu.toFixed(1))}
        ${barCellHTML(p.mem, maxMem, p.mem, p.mem.toFixed(1))}
        ${barCellHTML(p.rss, maxRss, ramPct, fmtBytes(p.rss))}
        <td class="proc-cmd" title="${escapeHtml(p.command)}">${escapeHtml(p.command)}</td>
        <td class="proc-kill-cell"><button class="proc-kill-btn" data-pid="${p.pid}" data-cmd="${escapeHtml(p.command)}" title="Kill PID ${p.pid}">&times;</button></td>
      `;
      const btn = tr.querySelector(".proc-kill-btn");
      if (btn) wireKillButton(btn);
    }
    frag.appendChild(tr);
  }

  existing.forEach((tr, pid) => {
    if (!seen.has(pid)) tr.remove();
  });

  tbody.appendChild(frag);
}

function renderStats(stats, statsContent) {
  if (!stats || !statsContent) return;
  lastStats = stats;
  ensureStatsStructure(statsContent);

  const cpuPct = (stats.cpu.usage * 100).toFixed(0);
  $("st-cpu-pct").textContent = cpuPct;
  const cpuBar = $("st-cpu-bar");
  cpuBar.style.width = cpuPct + "%";
  cpuBar.className = "sb-stats-fill " + barClass(stats.cpu.usage);
  $("st-cpu-cores").textContent = stats.cpu.cores;
  const loadStr = stats.loadAvg && stats.loadAvg.length > 0
    ? stats.loadAvg.map(l => l.toFixed(2)).join(" ")
    : "—";
  $("st-cpu-load").textContent = loadStr;
  $("st-cpu-model").textContent = stats.cpu.model;

  const memPct = (stats.memory.usage * 100).toFixed(0);
  $("st-mem-pct").textContent = memPct;
  const memBar = $("st-mem-bar");
  memBar.style.width = memPct + "%";
  memBar.className = "sb-stats-fill " + barClass(stats.memory.usage);
  $("st-mem-used").textContent = fmtBytes(stats.memory.used);
  $("st-mem-total").textContent = fmtBytes(stats.memory.total);
  $("st-mem-free").textContent = fmtBytes(stats.memory.free);

  const diskCard = $("st-disk-card");
  if (stats.disk) {
    diskCard.style.display = "";
    const diskPct = (stats.disk.usage * 100).toFixed(0);
    $("st-disk-pct").textContent = diskPct;
    const diskBar = $("st-disk-bar");
    diskBar.style.width = diskPct + "%";
    diskBar.className = "sb-stats-fill " + barClass(stats.disk.usage);
    $("st-disk-used").textContent = fmtBytes(stats.disk.used);
    $("st-disk-total").textContent = fmtBytes(stats.disk.total);
    $("st-disk-avail").textContent = fmtBytes(stats.disk.available);
  } else {
    diskCard.style.display = "none";
  }

  $("st-sys-host").textContent = stats.hostname;
  $("st-sys-platform").textContent = stats.platform;
  $("st-sys-uptime").textContent = fmtUptime(stats.uptime);
  $("st-sys-shells").textContent = `${stats.shells.count} (${stats.shells.running} running)`;

  updateProcTable();
}

export function startStatsPolling(els) {
  const { statsOverlay, statsContent, sbStatsBtn, statsClose } = els;
  if (statsTimer) clearInterval(statsTimer);
  const poll = () => { fetchStats().then((s) => renderStats(s, statsContent)); };
  poll();
  statsTimer = setInterval(() => {
    if (isModalOpen(statsOverlay)) {
      poll();
    }
  }, 3000);

  // Stats button + modal events
  if (sbStatsBtn) {
    sbStatsBtn.addEventListener("click", () => {
      haptic("tap");
      openModal(statsOverlay);
      poll();
    });
  }
  wireModal({ overlay: statsOverlay, closeBtn: statsClose });
}
